import { useState, useMemo } from 'react';
import { useMetaData, useApi } from '../hooks/useApi';
import { useCategory } from '../context/CategoryContext';
import { useMonth } from '../context/MonthContext';
import { CategoryBehaviour } from '../utils/api';
import { DualAxisBarLine, ChartCard, ChartLoader, ChartError } from '../components/Charts';
import { COLORS, fmtMonth } from '../utils/theme';

// Last 6 months ending at the selected dashboard month (ISO strings sort lexically)
function window6(allMonths, endMonth) {
  return allMonths.filter(m => !endMonth || m <= endMonth).slice(-6);
}

export default function CategoryBehaviourView() {
  const { category } = useCategory();
  const { month }    = useMonth();
  const { sessionTypes, latestMonth, loading: metaLoading } = useMetaData();

  const [sessionType, setSessionType] = useState('open_app');
  const effectiveCat  = category || 'ALL';
  const selectedMonth = month || latestMonth;

  const trend = useApi(
    () => CategoryBehaviour.trend({ category: effectiveCat, session_type: sessionType }),
    { effectiveCat, sessionType },
  );

  const months = useMemo(
    () => window6((trend.data || []).map(r => r.month_id), selectedMonth),
    [trend.data, selectedMonth],
  );
  const rowsByMonth = useMemo(
    () => Object.fromEntries((trend.data || []).map(r => [r.month_id, r])),
    [trend.data],
  );

  if (metaLoading) return <div className="loading-state"><div className="spinner" /> Loading…</div>;

  return (
    <div>
      <div className="filters-bar">
        <span className="filter-label">Session</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="trends-section-label">Category Behaviour — How is the category growing?</div>

      <ChartCard
        title="Monthly Trend — Users, Sessions & Penetration"
        sub={`${effectiveCat} · ${sessionType} · penetration = category users ÷ panel size`}
      >
        {trend.loading && <ChartLoader height={320} />}
        {trend.error   && <ChartError message={trend.error} height={320} />}
        {!trend.loading && !trend.error && months.length > 0 && (
          <DualAxisBarLine
            categories={months.map(fmtMonth)}
            series1={{ name: 'Active Users',  color: COLORS.teal,  data: months.map(m => rowsByMonth[m]?.active_users ?? null) }}
            series2={{ name: 'Sessions',      color: COLORS.amber, data: months.map(m => rowsByMonth[m]?.total_15min_sessions ?? null) }}
            line={{    name: 'Penetration %', color: COLORS.blue,  data: months.map(m => rowsByMonth[m]?.penetration_pct ?? null) }}
            height={320}
          />
        )}
        {!trend.loading && !trend.error && months.length === 0 && <div className="empty-state">No data</div>}
      </ChartCard>
    </div>
  );
}
